import {
    TbCalendarDue,
    TbCalendarStats,
    TbCalendarTime,
    TbFileText,
    TbFileTypePdf,
    TbMail,
    TbRepeat,
    TbUserCheck,
    TbUsers,
    TbWebhook,
} from 'react-icons/tb';

import { Member, Report } from '@/types';
import type { ScheduleDropdownOption } from '@/shared/components/dropdowns/ScheduleOptionDropdown';
import {
    ReportSchedule,
    ReportScheduleAttachmentType,
    ReportScheduleFrequency,
    ReportScheduleMethod,
    ReportScheduleRecipientMode,
} from '@/types/api/modules';
import { dayjs } from '@/utilities';

export const frequencyOptions: Array<ScheduleDropdownOption<ReportScheduleFrequency>> = [
    { value: 'day', label: 'Day', description: 'Deliver every day', icon: <TbCalendarDue size='0.875rem' /> },
    {
        value: 'weekday',
        label: 'Weekday',
        description: 'Deliver Monday through Friday',
        icon: <TbCalendarStats size='0.875rem' />,
    },
    { value: 'week', label: 'Week', description: 'Deliver once each week', icon: <TbCalendarTime size='0.875rem' /> },
    {
        value: 'twoWeeks',
        label: '2 Weeks',
        description: 'Deliver every other week',
        icon: <TbRepeat size='0.875rem' />,
    },
    { value: 'month', label: 'Month', description: 'Deliver monthly', icon: <TbCalendarStats size='0.875rem' /> },
    { value: 'threeMonths', label: '3 Months', description: 'Deliver quarterly', icon: <TbRepeat size='0.875rem' /> },
    {
        value: 'sixMonths',
        label: '6 Months',
        description: 'Deliver twice per year',
        icon: <TbRepeat size='0.875rem' />,
    },
    {
        value: 'nineMonths',
        label: '9 Months',
        description: 'Deliver every nine months',
        icon: <TbRepeat size='0.875rem' />,
    },
    { value: 'year', label: 'Year', description: 'Deliver annually', icon: <TbCalendarStats size='0.875rem' /> },
];

export const deliveryMethodOptions: Array<ScheduleDropdownOption<ReportScheduleMethod>> = [
    { value: 'email', label: 'Email', description: 'Send to collaborators', icon: <TbMail size='0.875rem' /> },
    {
        value: 'webhook',
        label: 'Webhook',
        description: 'POST to a subscriber URL',
        icon: <TbWebhook size='0.875rem' />,
    },
];

export const attachmentTypeOptions: Array<ScheduleDropdownOption<ReportScheduleAttachmentType>> = [
    { value: 'pdf', label: 'PDF', description: 'Portable report document', icon: <TbFileTypePdf size='0.875rem' /> },
    { value: 'docx', label: 'DOCX', description: 'Editable report document', icon: <TbFileText size='0.875rem' /> },
];

export const recipientModeOptions: Array<ScheduleDropdownOption<ReportScheduleRecipientMode>> = [
    {
        value: 'allCollaborators',
        label: 'All Collaborators',
        description: 'Every dashboard collaborator',
        icon: <TbUsers size='0.875rem' />,
    },
    {
        value: 'individualCollaborators',
        label: 'Individual Collaborators',
        description: 'Choose specific workspace members',
        icon: <TbUserCheck size='0.875rem' />,
    },
];

export const weekDays = [
    { value: 0, label: 'S' },
    { value: 1, label: 'M' },
    { value: 2, label: 'T' },
    { value: 3, label: 'W' },
    { value: 4, label: 'T' },
    { value: 5, label: 'F' },
    { value: 6, label: 'S' },
];

const defaultTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';

export const createDefaultSchedule = (dashboardId: string, reportIds: string[]): ReportSchedule => ({
    version: 1,
    source: 'dashboard',
    dashboardId,
    enabled: false,
    scope: 'entireDashboard',
    reportIds,
    frequency: 'week',
    timeOfDay: '08:00',
    timezone: defaultTimezone,
    weekDays: [1],
    startDate: dayjs().format('YYYY-MM-DD'),
    endDate: null,
    delivery: {
        method: 'email',
        attachmentType: 'pdf',
        recipients: {
            mode: 'allCollaborators',
            memberIds: [],
        },
    },
});

export const getReportSchedule = (dashboardId: string, reports: Report[]) => {
    for (const report of reports) {
        const schedule = report.schedulingSettings?.find?.(
            (item) => item?.source === 'dashboard' && item?.dashboardId === dashboardId,
        ) as ReportSchedule | undefined;

        if (schedule) {
            return schedule;
        }
    }

    return null;
};

export const getMemberName = (member: Member) => {
    const name = `${member.user?.firstName ?? ''} ${member.user?.lastName ?? ''}`.trim();

    return name || member.user?.emailAddress || 'Workspace Member';
};

export const formatNextDelivery = (value?: string | null) => {
    if (!value) {
        return 'Not scheduled';
    }

    return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
    }).format(new Date(value));
};

export const parseTimeOfDay = (timeOfDay: string) => dayjs(`2026-01-01T${timeOfDay || '08:00'}:00`);
